import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, SlidersHorizontalIcon } from 'lucide-react';
type StatCategory = 'both' | 'offense' | 'defense';
type MinuteSplit = 'both' | 'on' | 'off';
type Player = {
  id: string;
  name: string;
  position: string;
  minutesOn: number;
  minutesOff: number;
  paceOn: number;
  paceOff: number;
  possessions: number;
};
type StatRow = {
  label: string;
  on: number;
  off: number;
  suffix?: string;
};
const players: Player[] = [{
  id: 'brunson',
  name: 'Jalen Brunson',
  position: 'PG',
  minutesOn: 957.8,
  minutesOff: 287.2,
  paceOn: 72.6,
  paceOff: 70.8,
  possessions: 1913
}, {
  id: 'anunoby',
  name: 'OG Anunoby',
  position: 'SF',
  minutesOn: 836.6,
  minutesOff: 408.4,
  paceOn: 71.1,
  paceOff: 72.7,
  possessions: 1664
}, {
  id: 'hart',
  name: 'Josh Hart',
  position: 'SG',
  minutesOn: 902.4,
  minutesOff: 342.6,
  paceOn: 73.4,
  paceOff: 69.2,
  possessions: 1808
}, {
  id: 'randle',
  name: 'Julius Randle',
  position: 'PF',
  minutesOn: 771.9,
  minutesOff: 473.1,
  paceOn: 70.8,
  paceOff: 73.6,
  possessions: 1540
}, {
  id: 'hartenstein',
  name: 'Isaiah Hartenstein',
  position: 'C',
  minutesOn: 728.2,
  minutesOff: 516.8,
  paceOn: 72.2,
  paceOff: 71.5,
  possessions: 1453
}, {
  id: 'divincenzo',
  name: 'Donte DiVincenzo',
  position: 'SG',
  minutesOn: 684.6,
  minutesOff: 560.4,
  paceOn: 73.1,
  paceOff: 70.6,
  possessions: 1368
}, {
  id: 'mcbride',
  name: 'Miles McBride',
  position: 'PG',
  minutesOn: 414.8,
  minutesOff: 830.2,
  paceOn: 74.0,
  paceOff: 70.9,
  possessions: 826
}, {
  id: 'achiuwa',
  name: 'Precious Achiuwa',
  position: 'PF',
  minutesOn: 286.5,
  minutesOff: 958.5,
  paceOn: 71.7,
  paceOff: 72.1,
  possessions: 571
}];
const offensiveStats: StatRow[] = [{
  label: 'PTS',
  on: 80.9,
  off: 76.4
}, {
  label: 'RTG',
  on: 109.3,
  off: 103.7
}, {
  label: '2PM',
  on: 20.5,
  off: 18.4
}, {
  label: '2PA',
  on: 40.2,
  off: 37.8
}, {
  label: '2P%',
  on: 50.9,
  off: 48.7,
  suffix: '%'
}, {
  label: '3PM',
  on: 9.3,
  off: 8.5
}, {
  label: '3PA',
  on: 25.3,
  off: 24.1
}, {
  label: '3P%',
  on: 36.8,
  off: 35.2,
  suffix: '%'
}, {
  label: 'TS%',
  on: 54.7,
  off: 51.9,
  suffix: '%'
}, {
  label: 'FTM',
  on: 12.0,
  off: 10.7
}, {
  label: 'FTA',
  on: 19.2,
  off: 17.0
}, {
  label: 'FT%',
  on: 62.3,
  off: 62.8,
  suffix: '%'
}];
const defensiveStats: StatRow[] = [{
  label: 'OREB',
  on: 12.3,
  off: 10.6
}, {
  label: 'DREB',
  on: 22.0,
  off: 20.8
}, {
  label: 'REB',
  on: 34.3,
  off: 31.4
}, {
  label: 'OREB%',
  on: 33.0,
  off: 29.8,
  suffix: '%'
}, {
  label: 'DREB%',
  on: 65.0,
  off: 61.2,
  suffix: '%'
}, {
  label: 'AST',
  on: 16.7,
  off: 14.1
}, {
  label: 'TOV',
  on: 12.3,
  off: 14.0
}, {
  label: 'STL',
  on: 8.1,
  off: 6.9
}, {
  label: 'BLK',
  on: 1.9,
  off: 2.4
}, {
  label: 'BLKA',
  on: 1.8,
  off: 2.3
}, {
  label: 'PF',
  on: 24.2,
  off: 26.8
}, {
  label: 'DF',
  on: 20.5,
  off: 17.9
}];
export function TeamOnOff() {
  const [selectedPlayerId, setSelectedPlayerId] = useState('brunson');
  const [category, setCategory] = useState<StatCategory>('both');
  const [split, setSplit] = useState<MinuteSplit>('both');
  const [phase, setPhase] = useState('whole-match');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [location, setLocation] = useState('all');
  const [result, setResult] = useState('all');
  const [withTeammate, setWithTeammate] = useState('all');
  const [withoutTeammate, setWithoutTeammate] = useState('all');
  const selectedPlayer = useMemo(() => players.find(player => player.id === selectedPlayerId) ?? players[0], [selectedPlayerId]);
  const activeCategories = category === 'both' ? ['offense', 'defense'] : [category];
  const activeSplitLabel = split === 'both' ? 'ON CRT / OFF CRT' : split === 'on' ? 'ON CRT' : 'OFF CRT';
  const selectedPhase = phase === 'whole-match' ? 'Whole match' : `Quarter ${phase.slice(1)}`;
  return <section className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-800" data-mp="3kes1wduoqio5">
      <header className="border-b border-gray-200 px-5 py-4" data-id="element-801" data-mp="3pd3e2v8gd59j">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-802" data-mp="3pfpkusnirgqp">On/Off</h2>
        <p className="mt-0.5 text-xs text-gray-500" data-id="element-803" data-mp="3tghsjhgqtdkv">Analyze one player at a time across on-court and off-court possessions.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[238px_minmax(0,1fr)]" data-id="element-804" data-mp="31o0nuyzr643f">
        <aside className="border-b border-gray-200 bg-gray-50/60 lg:border-b-0 lg:border-r" aria-label="Player selection" data-id="element-805" data-mp="31pv6g6njxk4c">
          <div className="border-b border-gray-200 px-4 py-3" data-id="element-806" data-mp="3aop3tz8pieiy">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-807" data-mp="3an5ihu871dab">Players</h3>
            <p className="mt-1 text-xs text-gray-400" data-id="element-808" data-mp="3fgqa94j71b12">Select a player to update the analysis.</p>
          </div>
          <div className="max-h-[665px] overflow-y-auto p-2" data-id="element-809" data-mp="2hxyp19nljdvx">
            {players.map(player => {
            const isSelected = player.id === selectedPlayerId;
            const hasLowSample = player.possessions < 900;
            return <button key={player.id} type="button" onClick={() => setSelectedPlayerId(player.id)} className={`mb-1 w-full rounded-lg border px-3 py-2.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${isSelected ? 'border-blue-200 bg-blue-50' : 'border-transparent hover:border-gray-200 hover:bg-white'}`} aria-pressed={isSelected} data-id="element-810" data-mp="2ags25qh1aq6u">
                  <div className="flex items-center justify-between gap-2" data-id="element-811" data-mp="1rq0y94ftfrzu">
                    <span className={`text-sm font-semibold ${isSelected ? 'text-blue-800' : 'text-gray-800'}`} data-id="element-812" data-mp="1rnxvrkxh86e8">{player.name}</span>
                    <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 shadow-sm" data-id="element-813" data-mp="1hs4ooh2j153u">{player.position}</span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px]" data-id="element-814" data-mp="1dscanoud66r3">
                    <span className="text-gray-500" data-id="element-815" data-mp="18rycqzmh8d6s">{player.possessions.toLocaleString()} poss.</span>
                    {hasLowSample ? <span className="font-semibold text-amber-600" data-id="element-816" data-mp="z5x67p00kqx9">Limited sample</span> : <span className="font-semibold text-emerald-600" data-id="element-817" data-mp="136pfujcssxu2">Reliable sample</span>}
                  </div>
                </button>;
          })}
          </div>
          <div className="border-t border-gray-200 px-4 py-3 text-[11px] leading-relaxed text-gray-500" data-id="element-818" data-mp="u3y38g86rw0u">
            <span className="font-semibold text-amber-600" data-id="element-819" data-mp="1htscyox7l4gv">Limited sample</span> indicates fewer than 900 possessions.
          </div>
        </aside>

        <div className="min-w-0" data-id="element-820" data-mp="1mv7voabwyyw3">
          <div className="border-b border-gray-200 px-5 py-4" data-id="element-821" data-mp="184qwyyqnyn2w">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between" data-id="element-822" data-mp="1824ql2fiedm9">
              <div data-id="element-823" data-mp="1d18x1lb1tx8w">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400" data-id="element-824" data-mp="20swkft93qq6f">Selected player</p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-gray-900" data-id="element-825" data-mp="25k4tupafmbjl">{selectedPlayer.name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-x-7 gap-y-3 sm:grid-cols-3 xl:grid-cols-5" data-id="element-826" data-mp="3q8qqtb99w3c7">
                <Metric value="31" label="PG on crt" data-id="element-827" data-mp="3la5pt9m75fmp" />
                <Metric value={selectedPlayer.minutesOn.toFixed(1)} label="Min on crt" data-id="element-828" data-mp="2wy2lmvqutqj" />
                <Metric value="31" label="PG off crt" data-id="element-829" data-mp="2yse6al4l9hy" />
                <Metric value={selectedPlayer.minutesOff.toFixed(1)} label="Min off crt" data-id="element-830" data-mp="3v21p2o00rbsi" />
                <Metric value={selectedPlayer.paceOn.toFixed(1)} label="Pace (on crt)" data-id="element-831" data-mp="37c7b233yrh6o" />
              </div>
            </div>
          </div>

          <div className="border-b border-gray-100 bg-gray-50/70 px-5 py-3" data-id="element-832" data-mp="32e5xfxpb4a2l">
            <div className="flex flex-col gap-4" data-id="element-833" data-mp="3gdfa4neb0m1t">
              <div className="flex items-end gap-3 overflow-x-auto pb-1" data-id="element-834" data-mp="3bd141aw9jm6f">
                <div className="w-44 shrink-0" data-id="element-835" data-mp="3bf3ziag6q7ij">
                  <FilterGroup label="Stat category" data-id="element-836" data-mp="vklmt0t3ricn">
                    <SegmentedControl value={category} onChange={value => setCategory(value as StatCategory)} options={[{
                    value: 'offense',
                    label: 'OFF'
                  }, {
                    value: 'defense',
                    label: 'DEF'
                  }, {
                    value: 'both',
                    label: 'Both'
                  }]} data-id="element-837" data-mp="qmk2ydnurf04" />
                  </FilterGroup>
                </div>
                <div className="w-52 shrink-0" data-id="element-838" data-mp="1ogusjfqcu4jy">
                  <FilterGroup label="Minute split" data-id="element-839" data-mp="1tf4ohsjwl96m">
                    <SegmentedControl value={split} onChange={value => setSplit(value as MinuteSplit)} options={[{
                    value: 'on',
                    label: 'ON CRT'
                  }, {
                    value: 'off',
                    label: 'OFF CRT'
                  }, {
                    value: 'both',
                    label: 'Both'
                  }]} data-id="element-840" data-mp="1temkg9r01qo5" />
                  </FilterGroup>
                </div>
                <label className="block w-44 shrink-0" data-id="element-841" data-mp="d0wbaol0wc7n">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-842" data-mp="d1ehacxhm4sr">Phase of game</span>
                  <select value={phase} onChange={event => setPhase(event.target.value)} className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-843" data-mp="qn8i02lwdku">
                    <option value="whole-match" data-id="element-844" data-mp="3nt9uytkkhihf">Whole match</option>
                    <option value="q1" data-id="element-845" data-mp="oey34lhplh0o">Quarter 1</option>
                    <option value="q2" data-id="element-846" data-mp="jh4vdo0rceya">Quarter 2</option>
                    <option value="q3" data-id="element-847" data-mp="jho4cf8fjt06">Quarter 3</option>
                    <option value="q4" data-id="element-848" data-mp="ff1jiya6f8ch">Quarter 4</option>
                  </select>
                </label>
                <button type="button" onClick={() => setAdvancedOpen(open => !open)} aria-expanded={advancedOpen} className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${advancedOpen ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-849" data-mp="ah9hhbfti5uk">
                  <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-850" data-mp="1a1aoe8xfzerc" />
                  Advanced filter
                  {advancedOpen ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-851" data-mp="1j05tfhor2it2" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-852" data-mp="1iyl4c7l7fs2l" />}
                </button>
              </div>

              {advancedOpen && <div className="grid grid-cols-1 gap-3 border-t border-gray-200 pt-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-853" data-mp="1spxt2qbpi24e">
                  <AdvancedSelect label="Location" value={location} onChange={setLocation} options={[['all', 'Home & away'], ['home', 'Home only'], ['away', 'Away only']]} data-id="element-854" data-mp="26q8jxtd0ngpk" />
                  <AdvancedSelect label="Result" value={result} onChange={setResult} options={[['all', 'Wins & losses'], ['wins', 'Wins only'], ['losses', 'Losses only']]} data-id="element-855" data-mp="cbpizv84hl9n" />
                  <AdvancedSelect label="With selected teammate" value={withTeammate} onChange={setWithTeammate} options={[['all', 'Any teammate'], ['anunoby', 'OG Anunoby'], ['hart', 'Josh Hart'], ['randle', 'Julius Randle'], ['hartenstein', 'Isaiah Hartenstein']]} data-id="element-856" data-mp="8bx4z2zymmww" />
                  <AdvancedSelect label="Without selected teammate" value={withoutTeammate} onChange={setWithoutTeammate} options={[['all', 'Any teammate'], ['anunoby', 'OG Anunoby'], ['hart', 'Josh Hart'], ['randle', 'Julius Randle'], ['hartenstein', 'Isaiah Hartenstein']]} data-id="element-857" data-mp="3kkp4skwva3fb" />
                </div>}
            </div>
          </div>

          <div className="p-5" data-id="element-858" data-mp="3fnyp3yoz59uf">
            <div className="mb-4 flex items-center justify-between" data-id="element-859" data-mp="31n5qb1rn3sli">
              <div data-id="element-860" data-mp="31oz02pxjusxq">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-861" data-mp="36lz7b464y1qf">Team statistics</h4>
                <p className="mt-0.5 text-xs text-gray-500" data-id="element-862" data-mp="36ntruhdhvrtz">Per 100 possessions · active filters apply to the full split.</p>
              </div>
              <span className="text-xs font-semibold text-gray-500" data-id="element-863" data-mp="14uvqty9kdsxy">{selectedPhase}</span>
            </div>

            <div className={`grid gap-4 ${activeCategories.length === 2 ? 'md:grid-cols-2' : 'max-w-[620px]'}`} data-id="element-864" data-mp="12h0c3m4nzm3k">
              {activeCategories.includes('offense') && <StatTable stats={offensiveStats} split={split} data-id="element-865" data-mp="tf9aoth5q1fi" />}
              {activeCategories.includes('defense') && <StatTable stats={defensiveStats} split={split} data-id="element-866" data-mp="ogfv8vib80cp" />}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type MetricProps = {
  value: string;
  label: string;
};
function Metric({
  value,
  label
}: MetricProps) {
  return <div data-id="element-867" data-mp="1h5dbyu6vhp3c">
      <p className="text-lg font-bold leading-none text-gray-900" data-id="element-868" data-mp="1h5n2qjzqs0a3">{value}</p>
      <p className="mt-1 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-869" data-mp="1cctyryc3kvz2">{label}</p>
    </div>;
}
type FilterGroupProps = {
  label: string;
  children: React.ReactNode;
};
function FilterGroup({
  label,
  children
}: FilterGroupProps) {
  return <div data-id="element-870" data-mp="203fvitgnittr">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-871" data-mp="202oagckokehq">{label}</span>
      {children}
    </div>;
}
type SegmentedControlProps = {
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
};
function SegmentedControl({
  value,
  onChange,
  options
}: SegmentedControlProps) {
  return <div className="flex h-9 rounded-md border border-gray-300 bg-white p-0.5" role="group" data-id="element-872" data-mp="2mco32pgpq3ya">
      {options.map(option => <button key={option.value} type="button" onClick={() => onChange(option.value)} className={`flex-1 rounded px-2 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${value === option.value ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-873" data-mp="2qcost699i1kn">
          {option.label}
        </button>)}
    </div>;
}
type AdvancedSelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
};
function AdvancedSelect({
  label,
  value,
  onChange,
  options
}: AdvancedSelectProps) {
  return <label className="block" data-id="element-874" data-mp="flrj1dhls282">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500" data-id="element-875" data-mp="fkyg6nlrlwv7">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-876" data-mp="alvnm2mz1j44">
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue} data-id="element-877" data-mp="3svffqlsbzajg">{optionLabel}</option>)}
      </select>
    </label>;
}
type StatTableProps = {
  stats: StatRow[];
  split: MinuteSplit;
};
function StatTable({
  stats,
  split
}: StatTableProps) {
  const showOn = split !== 'off';
  const showOff = split !== 'on';
  return <article className="overflow-hidden rounded-lg border border-gray-200" data-id="element-878" data-mp="rvc9j13jhmy">
      <table className="w-full text-sm" data-id="element-879" data-mp="1h1xmtqmrtjhv">
        <thead className="border-b border-gray-200 bg-gray-50 text-[10px] font-bold uppercase tracking-wide" data-id="element-880" data-mp="1c4xqw8jmxx4h">
          <tr data-id="element-881" data-mp="1kl6i60c3b90e">
            <th scope="col" className="px-4 py-3 text-left text-gray-500" data-id="element-882" data-mp="1kkmzghem8glb">Stat</th>
            {showOff && <th scope="col" className="px-4 py-3 text-right text-gray-900" data-id="element-883" data-mp="1onjl6gzgcsth">Defense</th>}
            {showOn && <th scope="col" className="px-4 py-3 text-right text-blue-700" data-id="element-884" data-mp="1aobi3d0650k0">Offense</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100" data-id="element-885" data-mp="1fnegwfpk2ajj">
          {stats.map(stat => <tr key={stat.label} className="hover:bg-gray-50" data-id="element-886" data-mp="23bxnmqsyif9i">
              <th scope="row" className="px-4 py-2 text-left text-xs font-semibold text-gray-600" data-id="element-887" data-mp="23ejx4vzer4ud">{stat.label}</th>
              {showOff && <td className="px-4 py-2 text-right font-semibold text-gray-800" data-id="element-888" data-mp="1tmwb5almeg0a">{stat.off.toFixed(1)}{stat.suffix ?? ''}</td>}
              {showOn && <td className="px-4 py-2 text-right font-semibold text-blue-700" data-id="element-889" data-mp="1yjwfnh2tnapa">{stat.on.toFixed(1)}{stat.suffix ?? ''}</td>}
            </tr>)}
        </tbody>
      </table>
    </article>;
}